import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { UserProfile } from "@shared/types";
import { CalendarEvent } from "@shared/types";
import { formatTime } from "@/lib/utils";
import { useLocation } from "@/contexts/LocationContext";
import { useOrganization } from "@/contexts/OrganizationContext";

interface CalendarsProps {
  user: UserProfile;
}

export default function Calendars({ user }: CalendarsProps) {
  const { selectedLocationIds } = useLocation();
  const selectedLocation = selectedLocationIds.length === 1 ? selectedLocationIds[0] : '';
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const { currentOrganizationId } = useOrganization();
  
  // Use currentOrganizationId from context if available, otherwise fall back to user.organizationId
  const organizationId = currentOrganizationId || user.organizationId;
  
  // Get events
  const { data: events, isLoading, isError } = useQuery<CalendarEvent[]>({
    queryKey: [`/api/calendar-events?locationId=${selectedLocation}&organizationId=${organizationId}`],
    enabled: !!selectedLocation && !!organizationId,
  });

  // Filter events for the selected date
  const getEventsForSelectedDate = () => {
    if (!displayEvents || !selectedDate) return [];
    
    return displayEvents.filter(event => {
      if (!event || !event.startTime) return false;
      try {
        const eventDate = new Date(event.startTime);
        return eventDate.toDateString() === selectedDate.toDateString();
      } catch (e) {
        console.error("Error parsing date in getEventsForSelectedDate:", e);
        return false;
      }
    });
  };

  const hasEventScope = !!selectedLocation && !!organizationId;
  const displayEvents = hasEventScope && !isError ? events || [] : [];
  const eventsForSelectedDate = getEventsForSelectedDate();

  // Helper function to check if a date has events
  const hasEvents = (date: Date | undefined) => {
    if (!date) return false;
    
    return displayEvents.some(event => {
      if (!event.startTime) return false;
      try {
        const eventDate = new Date(event.startTime);
        return eventDate.toDateString() === date.toDateString();
      } catch (e) {
        console.error("Error parsing date:", e);
        return false;
      }
    });
  };

  // Custom day renderer for the calendar
  const renderDay = (day: Date) => {
    const hasEventsOnDay = hasEvents(day);
    return (
      <div className="relative">
        <div>{day.getDate()}</div>
        {hasEventsOnDay && (
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-primary rounded-full"></div>
        )}
      </div>
    );
  };

  return (
    <div>
      <p role="note" className="mb-4 rounded bg-amber-50 p-3 text-amber-900">Calendar preview: events shown here are samples, not scheduled meetings. See Meeting Agendas for scheduled meetings.</p>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between mb-6">
        <div className="flex items-center space-x-2 mb-4 md:mb-0">
          <h1 className="text-2xl font-medium">Calendars</h1>
          <span 
            className="mdi mdi-information-outline text-neutral-500 dark:text-neutral-400 cursor-help" 
            title="Location and department-level scheduling and milestones"
          ></span>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          <Button>
            <span className="mdi mdi-plus mr-2"></span>
            New Event
          </Button>
        </div>
      </div>

      {/* Calendar Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="p-4 pb-0">
              <CardTitle className="text-lg">
                {selectedDate ? selectedDate.toLocaleString('default', { month: 'long', year: 'numeric' }) : 'Calendar'}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                className="rounded-md border"
                components={{
                  DayContent: ({ date }) => date ? renderDay(date) : <div></div>,
                }}
              />
            </CardContent>
          </Card>
        </div>
        
        {/* Events for Selected Date */}
        <div>
          <Card>
            <CardHeader className="p-4 pb-0">
              <CardTitle className="text-lg">
                {selectedDate ? selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) : 'Events'}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {!hasEventScope ? (
                <p role="status" className="py-8 text-center text-muted-foreground">
                  Select a single location to view calendar samples. Use the location selector in the header and select just one location. For scheduled meetings, see Meeting Agendas.
                </p>
              ) : isError ? (
                <p role="alert" className="py-8 text-center text-destructive">
                  Calendar samples could not be loaded. Please try again later.
                </p>
              ) : isLoading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, index) => (
                    <Skeleton key={index} className="h-24 w-full" />
                  ))}
                </div>
              ) : (
                <>
                  {eventsForSelectedDate.length > 0 ? (
                    <div className="space-y-4">
                      {eventsForSelectedDate.map((event) => (
                        <div key={event.id} className="border-l-4 border-primary pl-3 py-2">
                          <div className="flex justify-between items-start">
                            <h3 className="font-medium">{event.title}</h3>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-6 w-6">
                                    <span className="mdi mdi-information-outline text-neutral-500"></span>
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{event.description}</p>
                                  <p className="mt-1 text-xs">Attendees: {event.attendees.join(', ')}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <div className="text-sm mt-1">
                            <div className="flex items-center text-neutral-600 dark:text-neutral-300">
                              <span className="mdi mdi-clock-outline mr-1"></span>
                              {formatTime(event.startTime)} - {formatTime(event.endTime)}
                            </div>
                            <div className="flex items-center text-neutral-600 dark:text-neutral-300 mt-0.5">
                              <span className="mdi mdi-map-marker-outline mr-1"></span>
                              {event.location}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-8 text-center">
                      <span className="mdi mdi-calendar-blank text-4xl text-neutral-400 block mb-2"></span>
                      <p className="text-neutral-500 dark:text-neutral-400">No calendar samples for this day</p>
                      <Button variant="outline" className="mt-4">
                        <span className="mdi mdi-plus mr-1"></span>
                        Add Event
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
